import { useSeoMeta, useHead } from '@unhead/react'
import { useState } from 'react'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'

function ConcreteWeightCalculator() {
  useSeoMeta({
    title: 'Concrete Weight Calculator | Estimate Weight by Volume',
    description: 'Calculate concrete weight by volume. Free concrete weight estimator for project planning. Convert cubic yards or feet to pounds and tons.',
    ogTitle: 'Concrete Weight Calculator | Weight Estimator Tool',
    ogDescription: 'Estimate concrete weight by volume for any project.',
    ogUrl: 'https://www.concrete-calculator.com/concrete-weight-calculator/',
    ogType: 'website',
    twitterCard: 'summary_large_image'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/concrete-weight-calculator/' }
    ]
  })

  const [volumeType, setVolumeType] = useState('cubicYards')
  const [volume, setVolume] = useState('')
  const [concreteType, setConcreteType] = useState('standard')
  const [results, setResults] = useState<{
    pounds: number
    tons: number
    kilograms: number
    cubicYards: number
    cubicFeet: number
  } | null>(null)

  const calculate = () => {
    const vol = parseFloat(volume)
    if (isNaN(vol) || vol <= 0) {
      return
    }

    // Convert to cubic yards for calculation
    let cubicYards = vol
    if (volumeType === 'cubicFeet') {
      cubicYards = vol / 27
    }

    // Weight per cubic yard (pounds)
    const weightMap: { [key: string]: number } = {
      'standard': 4050,  // Standard concrete ~4,050 lbs/yd³
      'lightweight': 3000, // Lightweight concrete ~3,000 lbs/yd³
      'heavy': 4500      // Heavy aggregate ~4,500 lbs/yd³
    }

    const weightPerYard = weightMap[concreteType]
    const totalPounds = cubicYards * weightPerYard
    const totalTons = totalPounds / 2000
    const totalKg = totalPounds * 0.453592

    setResults({
      pounds: totalPounds,
      tons: totalTons,
      kilograms: totalKg,
      cubicYards: cubicYards,
      cubicFeet: cubicYards * 27
    })
  }

  const getExportData = (): ExportData => ({
    title: 'Concrete Weight Calculator Results',
    inputs: [
      { label: 'Volume Unit', value: volumeType === 'cubicYards' ? 'Cubic Yards' : 'Cubic Feet' },
      { label: 'Volume Amount', value: volume },
      { label: 'Concrete Type', value: concreteType === 'standard' ? 'Standard (~4,050 lbs/yd³)' : concreteType === 'lightweight' ? 'Lightweight (~3,000 lbs/yd³)' : 'Heavy Aggregate (~4,500 lbs/yd³)' }
    ],
    results: [
      { label: 'Volume (Cubic Yards)', value: `${results?.cubicYards.toFixed(2)} yd³` },
      { label: 'Volume (Cubic Feet)', value: `${results?.cubicFeet.toFixed(2)} ft³` },
      { label: 'Weight (Pounds)', value: `${results?.pounds.toFixed(0)} lbs` },
      { label: 'Weight (Tons)', value: `${results?.tons.toFixed(2)} tons` },
      { label: 'Weight (Kilograms)', value: `${results?.kilograms.toFixed(0)} kg` }
    ]
  })

  const reset = () => {
    setVolume('')
    setVolumeType('cubicYards')
    setConcreteType('standard')
    setResults(null)
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          Concrete Weight Calculator & Estimator
        </h1>
        
        <nav aria-label="Calculator Tools" className="mb-8">
          <a href="/" className="text-[#FF8C00] hover:underline">← Back to Home</a>
        </nav>

        {/* Calculator */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Estimate Concrete Weight</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="volumeType" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Volume Unit
              </label>
              <select
                id="volumeType"
                value={volumeType}
                onChange={(e) => setVolumeType(e.target.value)}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
              >
                <option value="cubicYards">Cubic Yards</option>
                <option value="cubicFeet">Cubic Feet</option>
              </select>
            </div>

            <div>
              <label htmlFor="volume" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Volume Amount
              </label>
              <input
                id="volume"
                type="number"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                placeholder="Enter volume"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="0.1"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="concreteType" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Concrete Type
              </label>
              <select
                id="concreteType"
                value={concreteType}
                onChange={(e) => setConcreteType(e.target.value)}
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
              >
                <option value="standard">Standard Concrete (~4,050 lbs/yd³)</option>
                <option value="lightweight">Lightweight Concrete (~3,000 lbs/yd³)</option>
                <option value="heavy">Heavy Aggregate (~4,500 lbs/yd³)</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              onClick={calculate}
              className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
            >
              Calculate Weight
            </button>
            <button
              onClick={reset}
              className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
            >
              Reset
            </button>
          </div>

          {results && (
            <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Weight Results</h3>
                <ExportButtons data={getExportData()} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Volume (Cubic Yards)</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicYards.toFixed(2)} yd³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Volume (Cubic Feet)</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicFeet.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Weight (Pounds)</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.pounds.toFixed(0)} lbs</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Weight (Tons)</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.tons.toFixed(2)} tons</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200 sm:col-span-2">
                  <p className="text-sm text-gray-600 mb-1">Weight (Kilograms)</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.kilograms.toFixed(0)} kg</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-gray-600">
            <p className="mb-2">
              <strong>How to use:</strong> Enter your concrete volume and select the type. Standard concrete weighs approximately 4,050 pounds per cubic yard when wet.
            </p>
            <p>
              <strong>Note:</strong> Weight varies based on aggregate type and moisture content. These are approximate values for planning purposes.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How much does 1 yard of concrete weigh?
              </h3>
              <p className="text-gray-700">
                One cubic yard of standard concrete weighs approximately 4,050 pounds (about 2 tons) when wet. The weight can vary between 3,000-4,500 lbs depending on the aggregate mix and moisture content.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                What factors affect concrete weight?
              </h3>
              <p className="text-gray-700">
                Concrete weight is affected by: aggregate type (lightweight vs. heavy stone), water content, air entrainment, and admixtures. Lightweight concrete can weigh as little as 3,000 lbs/yd³ while heavy aggregate concrete can exceed 4,500 lbs/yd³.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default ConcreteWeightCalculator
